/**
 * Confirmation card — RN counterpart of the web widget's
 * `components/steps/confirmation-step.tsx`.
 *
 * Terminal state for a flow: a status glyph, the copy the backend sent, and up
 * to four detail rows. The rows come from the payload when it carries
 * `details`, otherwise from the data the flow collected, filtered and ordered
 * by `PRIORITY_KEYS` so a booking always reads date → time → who.
 *
 * The framer-motion entrance survives as RN `Animated`: the same 100ms/150ms
 * stagger, and the same spring on the glyph (`stiffness: 400, damping: 15`
 * transfers one-to-one, since both libraries take real spring physics).
 */

import { useEffect, useRef, type FC } from "react";
import { Animated, StyleSheet, Text, View } from "react-native";
import { Calendar, Check, CircleX, Clock, Info, Mail, TriangleAlert, User, type LucideIcon } from "lucide-react-native";

import type { ConfirmationData } from "../../lib/component-types";
import { GradientFill, solidColor } from "../../lib/gradient";
import { mergeTheme } from "../../lib/theme-utils";
import { resolveFontFamily, useWidgetTheme } from "../../lib/widget-theme";

type FormValue = string | number | boolean | string[];

interface ConfirmationStepProps {
  data: ConfirmationData;
  collectedData?: Record<string, FormValue>;
}

/** A status glyph and the colour of the disc behind it. */
interface IconConfig {
  icon: LucideIcon;
  bgColor: string;
}

const ENTER_DURATION_MS = 300;
const TITLE_DELAY_MS = 100;
const DETAILS_DELAY_MS = 150;

/** Amber-500. The only status colour with no theme slot of its own. */
const WARNING_COLOR = "#f59e0b";

/** Web's `text-white`: the glyph always sits on a saturated disc. */
const GLYPH_COLOR = "#ffffff";

/** Which collected fields are worth echoing back, in reading order. */
const PRIORITY_KEYS = ["slot_date", "slot_time", "name", "email", "phone"];

const MAX_DETAILS = 4;

/** Fades a node in, optionally after `delayMs` — framer-motion's `delay`. */
const useEnter = (delayMs = 0): Animated.Value => {
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(progress, { toValue: 1, duration: ENTER_DURATION_MS, delay: delayMs, useNativeDriver: true }).start();
  }, [delayMs, progress]);

  return progress;
};

/** The glyph's `scale: 0 → 1` spring. */
const usePop = (): Animated.Value => {
  const scale = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.spring(scale, { toValue: 1, stiffness: 400, damping: 15, mass: 1, useNativeDriver: true }).start();
  }, [scale]);

  return scale;
};

const formatValue = (value: FormValue): string => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const getDetailIcon = (key?: string): LucideIcon | null => {
  if (!key) return null;
  if (key.includes("date")) return Calendar;
  if (key.includes("time")) return Clock;
  if (key.includes("email")) return Mail;
  if (key.includes("name")) return User;
  return null;
};

const ConfirmationStep: FC<ConfirmationStepProps> = ({ data, collectedData }) => {
  // Web reads this from the router's ThemeContext; RN resolves the same
  // widget→component mapping locally (see components/interactive-flow.tsx).
  const widgetTheme = useWidgetTheme();
  const theme = mergeTheme({
    primaryColor: widgetTheme.primaryColor,
    primaryTextColor: widgetTheme.primaryTextColor,
    backgroundColor: widgetTheme.backgroundColor,
    surfaceColor: widgetTheme.surfaceColor,
    textColor: widgetTheme.textColor,
    mutedTextColor: widgetTheme.mutedTextColor,
    borderColor: widgetTheme.borderColor,
  });
  const fontFamily = resolveFontFamily(theme.fontFamily);

  const cardProgress = useEnter();
  const glyphScale = usePop();
  const titleProgress = useEnter(TITLE_DELAY_MS);
  const detailsProgress = useEnter(DETAILS_DELAY_MS);

  const iconType = data.icon || "success";

  const getIconConfig = (): IconConfig => {
    switch (iconType) {
      case "success":
        return { icon: Check, bgColor: theme.successColor };
      case "info":
        return { icon: Info, bgColor: theme.primaryColor };
      case "warning":
        return { icon: TriangleAlert, bgColor: WARNING_COLOR };
      case "error":
        return { icon: CircleX, bgColor: theme.errorColor };
      default:
        return { icon: Check, bgColor: theme.successColor };
    }
  };

  const { icon: Icon, bgColor } = getIconConfig();
  const displayDetails = data.details || [];

  const formattedCollectedData = collectedData
    ? Object.entries(collectedData)
        .filter(([key]) => PRIORITY_KEYS.includes(key))
        .sort((a, b) => PRIORITY_KEYS.indexOf(a[0]) - PRIORITY_KEYS.indexOf(b[0]))
        .map(([key, value]) => ({
          key,
          label: key
            .replace(/slot_/g, "")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()),
          value: formatValue(value),
        }))
    : [];

  const detailsToShow: Array<{ label: string; value: string; key?: string }> = displayDetails.length > 0 ? displayDetails : formattedCollectedData;

  return (
    <Animated.View style={[styles.root, { opacity: cardProgress }]}>
      <View style={styles.header}>
        <Animated.View style={[styles.glyph, { transform: [{ scale: glyphScale }] }]}>
          <GradientFill color={bgColor} style={StyleSheet.absoluteFill} />
          <Icon size={18} color={GLYPH_COLOR} />
        </Animated.View>

        <Animated.View style={[styles.headerText, { opacity: titleProgress, transform: [{ translateX: titleProgress.interpolate({ inputRange: [0, 1], outputRange: [-8, 0] }) }] }]}>
          <Text style={[styles.title, { color: theme.textColor, fontFamily }]}>{data.title}</Text>
          {data.message ? <Text style={[styles.message, { color: theme.mutedTextColor, fontFamily }]}>{data.message}</Text> : null}
        </Animated.View>
      </View>

      {detailsToShow.length > 0 && (
        <Animated.View style={[styles.details, { backgroundColor: theme.surfaceColor, opacity: detailsProgress, transform: [{ translateY: detailsProgress.interpolate({ inputRange: [0, 1], outputRange: [8, 0] }) }] }]}>
          {detailsToShow.slice(0, MAX_DETAILS).map((detail, index) => {
            const DetailIcon = detail.key ? getDetailIcon(detail.key) : null;
            return (
              <View key={index} style={[styles.detailRow, index === 0 ? null : { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: solidColor(theme.borderColor) }]}>
                {DetailIcon ? <DetailIcon size={14} color={solidColor(theme.mutedTextColor)} /> : null}
                <Text style={[styles.detailLabel, { color: theme.mutedTextColor, fontFamily }]}>{detail.label}</Text>
                <Text numberOfLines={1} accessibilityLabel={detail.value} style={[styles.detailValue, { color: theme.textColor, fontFamily }]}>
                  {detail.value}
                </Text>
              </View>
            );
          })}
        </Animated.View>
      )}
    </Animated.View>
  );
};

export default ConfirmationStep;

const styles = StyleSheet.create({
  root: { paddingVertical: 8 },
  header: { flexDirection: "row", alignItems: "flex-start", gap: 12, marginBottom: 16 },
  glyph: { width: 36, height: 36, borderRadius: 9999, alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" },
  headerText: { flex: 1, paddingTop: 2 },
  title: { fontSize: 14, lineHeight: 17.5, fontWeight: "600" },
  message: { fontSize: 12, lineHeight: 19.5, marginTop: 4 },
  details: { borderRadius: 8, overflow: "hidden" },
  detailRow: { flexDirection: "row", alignItems: "center", gap: 10, paddingHorizontal: 12, paddingVertical: 10 },
  detailLabel: { fontSize: 12, lineHeight: 16, flexShrink: 0 },
  // `ml-auto` — pushes the value to the trailing edge, and lets it truncate
  // rather than shove the label out of the row.
  detailValue: { fontSize: 12, lineHeight: 16, fontWeight: "500", marginLeft: "auto", flexShrink: 1 },
});
